import React from 'react';

import { enqueueSnackbar } from 'notistack';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button, InfoBox } from '@infoods/lifebase-ui';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ControlPointRoundedIcon from '@mui/icons-material/ControlPointRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import classes from './IngredientsCard.module.scss';
import { WidgetEmptyPlaceholder } from '../../../../shared/components/ui-components';
import { openModal } from '../../../../shared/utils/reactUtils/openModal/openModal';
import { FoodNutrientsCard } from '../../../components/FoodNutrientsCard/FoodNutrientsCard';
import { DatabaseFoodItem } from '../../../interfaces/DatabaseFoodItem';
import { FoodListItem } from '../../../interfaces/FoodListItem';
import { RecipeFormValues } from '../interfaces/RecipeFormValues';
import { foodToIngredientFormValue } from '../utils/foodToIngredientFormValue/foodToIngredientFormValue';
import { getIngredientTotals } from '../utils/getIngredientTotals/getIngredientTotals';
import { isSameIngredient } from '../utils/isSameIngredient/isSameIngredient';
import { AddIngredientModal } from './AddIngredientModal/AddIngredientModal';
import { IngredientDetailsModal } from './IngredientDetailsModal/IngredientDetailsModal';

const IngredientsCard = () => {
  const { control, getValues } = useFormContext<RecipeFormValues>();
  // `fields[].id` is the field-array key, not the ingredient id
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'ingredients',
  });

  const handleFoodSelect = (
    food: FoodListItem | DatabaseFoodItem,
    closeSearchModal: () => void,
  ) => {
    const ingredient = foodToIngredientFormValue(food);

    if (getValues('ingredients').some((added) => isSameIngredient(added, ingredient))) {
      enqueueSnackbar('This ingredient is already added to recipe.', {
        variant: 'warning',
        autoHideDuration: 3000,
      });
      return;
    }

    // one shared modal root - the search must unmount before the details modal mounts
    closeSearchModal();
    openModal((closeModal) => (
      <IngredientDetailsModal
        mode="add"
        ingredient={ingredient}
        onSave={append}
        onClose={closeModal}
      />
    ));
  };

  const handleAddIngredient = () =>
    openModal((closeModal) => (
      <AddIngredientModal
        onFoodSelect={(food) => handleFoodSelect(food, closeModal)}
        onClose={closeModal}
      />
    ));

  const handleEditIngredient = (index: number) =>
    openModal((closeModal) => (
      <IngredientDetailsModal
        mode="edit"
        ingredient={getValues(`ingredients.${index}`)}
        onSave={(ingredient) => update(index, ingredient)}
        onDelete={() => remove(index)}
        onClose={closeModal}
      />
    ));

  return (
    <InfoBox
      title="Ingredients"
      adornment={
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={handleAddIngredient}
          data-test-id="add-ingredient-btn"
        >
          Add ingredient
        </Button>
      }
    >
      {fields.length ? (
        <div className={classes.cardGrid}>
          {fields.map((field, index) => (
            <FoodNutrientsCard
              key={field.id}
              food={{
                name: field.name,
                amount: field.amount,
                amountUnit: field.amountUnit,
                ...getIngredientTotals(field.perUnit, field.amount),
              }}
              actionIcon={
                <span className={classes.editIcon}>
                  <EditRoundedIcon fontSize="small" />
                </span>
              }
              onClick={() => handleEditIngredient(index)}
            />
          ))}
        </div>
      ) : (
        <WidgetEmptyPlaceholder
          title="No ingredients added yet"
          icon={<ControlPointRoundedIcon fontSize="large" />}
          text='Click "Add ingredient" to add ingredients.'
        />
      )}
    </InfoBox>
  );
};

export { IngredientsCard };
